import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { ArrowUpRight, Boxes, FileText, Loader2, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge, LangBadge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator, Skeleton } from '@/components/ui/misc'
import { getTermConcept } from '@/lib/api/terms/get-term-extras'
import { documentFileUrl, type DocumentTerm } from '@/lib/api/documents/get-documents'
import { cn } from '@/lib/utils'
import { termBaseSearch } from '@/hooks/use-term-base-state'
import { useLocalizedPath } from '@/lib/i18n/routes'
import { LANGUAGES, type ConceptDefinition } from '@/types/terms'

/**
 * What one highlighted term in a document actually is: its own record, and
 * the concept it belongs to with every sibling term and definition.
 *
 * A drawer rather than a route: the reader's place in the text is the whole
 * point, and sending them to the Terms view to answer "what is this?" loses
 * it. The two buttons at the bottom are for when they do want to leave.
 */
export function DocumentTermDrawer({
  term,
  collectionId,
  onClose,
}: {
  /** null = closed. */
  term: DocumentTerm | null
  /** Carried into the links, so a new tab opens on the same collection. */
  collectionId: number | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const path = useLocalizedPath()
  // Real links, not buttons: a plain click navigates in place, and
  // cmd/ctrl/middle-click opens the term base in a second tab at that exact
  // term or concept — which is the point of looking something up while you
  // are reading a document.
  const termHref = (lemma: string, lang: string) =>
    path('termBase') +
    termBaseSearch({ mode: 'terminid', term: lemma, termLang: lang, collectionId })
  const conceptHref = (conceptId: number) =>
    path('termBase') + termBaseSearch({ mode: 'moisted', conceptId, collectionId })
  const concept = useQuery({
    queryKey: ['term-concept', term?.term_id, 'terms'],
    queryFn: () => getTermConcept(term!.term_id, 'terms'),
    enabled: !!term,
  })

  if (!term) return null

  const members = concept.data?.terms ?? []
  const byLang = LANGUAGES.map((l) => [l, members.filter((m) => m.lang === l)] as const)
    .filter(([, ms]) => ms.length)
  // Languages the grouping produced that are not the project's three — a
  // stray tag is worth seeing rather than hiding.
  const otherLangs = [...new Set(members.map((m) => m.lang))]
    .filter((l) => !LANGUAGES.includes(l as (typeof LANGUAGES)[number]))
  const defs = concept.data?.definitions ?? []
  const quotes = defs.filter((d) => d.type.startsWith('seletus'))
  const drafts = defs.filter((d) => d.type === 'autogen')

  return (
    <aside className="flex w-96 shrink-0 flex-col border-l border-border bg-card">
      <div className="flex shrink-0 items-start gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-[15px] font-semibold">{term.lemma}</span>
            <LangBadge lang={term.lang} />
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
            {term.domain && <Badge variant="outline" size="sm">{term.domain}</Badge>}
            {term.term_type && term.term_type !== 'term' && (
              <Badge variant="default" size="sm">{term.term_type}</Badge>
            )}
            <span>{t('documents.drawer.inThisDocument', { count: term.mentions.length })}</span>
          </div>
        </div>
        <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t('common.close')}>
          <X />
        </Button>
      </div>

      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {concept.isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-4 w-full" />)}
          </div>
        ) : !concept.data ? (
          <p className="py-4 text-[12px] text-muted-foreground">{t('documents.drawer.noConcept')}</p>
        ) : (
          <>
            <Link
              to={conceptHref(concept.data.concept_id)}
              className="mb-1 flex w-fit items-center gap-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground hover:text-primary hover:underline"
              title={t('documents.drawer.openConceptHint')}
            >
              <Boxes className="size-3" />
              {t('documents.drawer.concept', { id: concept.data.concept_id })}
            </Link>
            {byLang.map(([lang, ms]) => (
              <div key={lang} className="mb-1.5 flex gap-2">
                <LangBadge lang={lang} className="mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1 text-[13px] leading-relaxed">
                  {ms.map((m, i) => (
                    <span key={`${m.value}-${i}`}>
                      {i > 0 && <span className="text-muted-foreground"> · </span>}
                      <Link
                        to={termHref(m.value, m.lang)}
                        className={cn(
                          'hover:text-primary hover:underline',
                          // The term the reader clicked, picked out of its siblings.
                          m.value.toLowerCase() === term.lemma.toLowerCase() && m.lang === term.lang
                            ? 'rounded bg-highlight px-0.5 font-semibold text-highlight-foreground'
                            : m.is_preferred && 'font-medium',
                        )}
                        title={t('documents.drawer.openTermHint')}
                      >
                        {m.value}
                      </Link>
                    </span>
                  ))}
                </div>
              </div>
            ))}
            {otherLangs.map((lang) => (
              <div key={lang} className="mb-1.5 flex gap-2">
                <Badge variant="warning" size="sm" className="mt-0.5 shrink-0">{lang}</Badge>
                <div className="min-w-0 flex-1 text-[13px]">
                  {members.filter((m) => m.lang === lang).map((m) => m.value).join(' · ')}
                </div>
              </div>
            ))}

            {!!quotes.length && (
              <>
                <Separator className="my-2" />
                <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t('documents.drawer.quotes', { count: quotes.length })}
                </div>
                {quotes.slice(0, 3).map((d, i) => <DefinitionRow key={i} d={d} />)}
              </>
            )}
            {!!drafts.length && (
              <>
                <Separator className="my-2" />
                <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t('documents.drawer.drafts')}
                </div>
                {drafts.map((d, i) => <DefinitionRow key={i} d={d} />)}
              </>
            )}
          </>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap gap-1.5 border-t border-border px-3 py-2">
        <Button variant="outline" size="sm" asChild>
          <Link to={termHref(term.lemma, term.lang)}>
            <ArrowUpRight />
            {t('documents.drawer.openTerm')}
          </Link>
        </Button>
        {concept.data && (
          <Button variant="outline" size="sm" asChild>
            <Link to={conceptHref(concept.data.concept_id)}>
              <Boxes />
              {t('documents.drawer.openConcept')}
            </Link>
          </Button>
        )}
        {concept.isFetching && <Loader2 className="size-4 animate-spin self-center text-muted-foreground" />}
      </div>
    </aside>
  )
}

/** One definition: the text, its language, and where it came from. */
function DefinitionRow({ d }: { d: ConceptDefinition }) {
  const { t } = useTranslation()
  return (
    <div className="mb-2 border-l-2 border-border pl-2">
      <p className="text-[12px] leading-snug">{d.definition}</p>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[10px] text-muted-foreground">
        <LangBadge lang={d.lang} />
        {d.source && <span className="truncate">{d.source}</span>}
        {!!d.page && <span>{t('common.page', { page: d.page })}</span>}
        {d.has_file && d.document_id && (
          <a
            href={documentFileUrl(d.document_id, d.page)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
            title={t('mentions.openPdf')}
          >
            <FileText className="size-3" />
          </a>
        )}
        {d.model && <span className="italic">{d.model}</span>}
      </div>
    </div>
  )
}
